/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Dimensions,
} from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Modal from '@components/modal/Modal';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import { isWeb, isMobileWeb, isNative } from '@global/screens';
import { Portal, PortalHost } from '@gorhom/portal';
import { parseDate } from '@global/helpers';

const dimension = Dimensions.get('window');
const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const toMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dateKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const buildGrid = (fulldate) => {
  const dates = [[], [], [], [], [], [], []];
  let index = 0;
  const startOfMonth = new Date(fulldate.getFullYear(), fulldate.getMonth(), 1);
  const endOfMonth = new Date(
    fulldate.getFullYear(),
    fulldate.getMonth() + 1,
    0,
  );
  for (let i = 0; i < startOfMonth.getDay(); i++) {
    dates[0].push('');
  }
  for (let i = 0; i < endOfMonth.getDate(); i++) {
    const date = new Date(fulldate.getFullYear(), fulldate.getMonth(), i + 1);
    dates[index].push(date);
    if (dates[index].length >= 7) {
      index++;
    }
  }
  return dates;
};

const buildYears = () => {
  const years = [];
  let y = 2012;
  const end = new Date().getFullYear() + 10;
  while (y <= end) {
    years.push(y++);
  }
  return years;
};
const YEARS = buildYears();

const SociollaMultiDatePicker = (props) => {
  const { visible, onClose, onChange, values = [] } = props;

  const [selected, setSelected] = useState([]);
  const [fulldate, setFullDate] = useState(new Date());
  const [showYearList, setShowYearList] = useState(false);
  const [lists, setLists] = useState([[], [], [], [], [], [], []]);
  const [dateHeight, setDateHeight] = useState(30);
  const [dateHeightChanged, setDateHeightChanged] = useState(false);

  useEffect(() => {
    if (visible) {
      const initial = (values || []).map((d) => toMidnight(new Date(d)));
      setSelected(initial);
      setFullDate(initial.length ? new Date(initial[0]) : new Date());
      setShowYearList(false);
    }
  }, [visible]);

  useEffect(() => {
    setLists(buildGrid(fulldate));
  }, [fulldate]);

  const selectedKeys = new Set(selected.map(dateKey));

  const toggleDate = (date) => {
    if (!date) {
      return;
    }
    const d = toMidnight(date);
    const key = dateKey(d);
    if (selectedKeys.has(key)) {
      setSelected(selected.filter((s) => dateKey(s) !== key));
    } else {
      setSelected([...selected, d].sort((a, b) => a - b));
    }
  };

  const isSelected = (date) =>
    date && selectedKeys.has(dateKey(toMidnight(date)));

  const headerText = () => {
    if (!selected.length) {
      return 'Select dates';
    }
    if (selected.length === 1) {
      return parseDate(selected[0], 'd MMM yyyy');
    }
    return `${selected.length} days selected`;
  };

  const onLayoutItem = (e) => {
    try {
      setTimeout(() => {
        if (!dateHeightChanged && !isNative) {
          setDateHeightChanged(true);
          setDateHeight(e.nativeEvent.target.clientWidth);
        }
      }, 500);
    } catch (_err) {}
  };

  const arrowSize = { width: 8, height: 12 };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      spaceFromTop={'10%'}
      maxWidth={isWeb ? 280 : isMobileWeb ? 'calc(100% - 30px)' : 260}
      onClose={onClose}>
      <View style={styles.datepicker}>
        <View style={styles.datepickerTop}>
          <View>
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.yearContainer}
              onPress={() => setShowYearList(true)}>
              <SociollaText type="bold" style={styles.yearText}>
                {fulldate.getFullYear()}
              </SociollaText>
            </TouchableOpacity>
            {showYearList ? (
              <Portal hostName="portal_year_multi">
                <View style={styles.yearList}>
                  <ScrollView>
                    {YEARS.map((yr, idx) => (
                      <TouchableOpacity
                        key={idx}
                        activeOpacity={0.6}
                        style={styles.yearItem}
                        onPress={() => {
                          setFullDate(new Date(yr, fulldate.getMonth(), 1));
                          setShowYearList(false);
                        }}>
                        <SociollaText type="bold" style={{ fontSize: 14 }}>
                          {yr}
                        </SociollaText>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </Portal>
            ) : null}
          </View>
          <SociollaText
            type="medium"
            style={styles.headerText}
            numberOfLines={1}>
            {headerText()}
          </SociollaText>
        </View>

        <View style={styles.monthRow}>
          <TouchableOpacity
            style={styles.arrowBtn}
            activeOpacity={0.6}
            onPress={() =>
              setFullDate(
                new Date(fulldate.getFullYear(), fulldate.getMonth() - 1, 1),
              )
            }>
            <Image
              style={arrowSize}
              width={arrowSize.width}
              height={arrowSize.height}
              svg={true}
              source={require('@assets/img/icons/arrow-left-grey.svg')}
            />
          </TouchableOpacity>
          <SociollaText style={{ fontSize: 12, color: Colors.black }}>
            {monthNames[fulldate.getMonth()]} {fulldate.getFullYear()}
          </SociollaText>
          <TouchableOpacity
            style={styles.arrowBtn}
            activeOpacity={0.6}
            onPress={() =>
              setFullDate(
                new Date(fulldate.getFullYear(), fulldate.getMonth() + 1, 1),
              )
            }>
            <Image
              style={arrowSize}
              width={arrowSize.width}
              height={arrowSize.height}
              svg={true}
              source={require('@assets/img/icons/arrow-right-grey.svg')}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.daysRow}>
          {dayLetters.map((d, idx) => (
            <View key={idx} style={[styles.dateCell, styles.rowCenter]}>
              <SociollaText
                type="bold"
                style={{ fontSize: 12, color: Colors.grey1 }}>
                {d}
              </SociollaText>
            </View>
          ))}
        </View>

        <View style={styles.datesGrid}>
          {lists.map((row, rowIdx) =>
            row.map((date, colIdx) => {
              const active = isSelected(date);
              return (
                <TouchableOpacity
                  key={`${rowIdx}-${colIdx}`}
                  activeOpacity={0.6}
                  style={[
                    styles.dateCell,
                    styles.rowCenter,
                    !isNative ? { height: dateHeight } : {},
                    { borderRadius: 60 },
                    active ? { backgroundColor: Colors.blue0 } : {},
                  ]}
                  onPress={() => toggleDate(date)}
                  onLayout={onLayoutItem}
                  disabled={!date}>
                  <SociollaText
                    type={active ? 'bold' : 'regular'}
                    style={{ fontSize: 12, color: active ? Colors.white : {} }}>
                    {date ? date.getDate() : ''}
                  </SociollaText>
                </TouchableOpacity>
              );
            }),
          )}
        </View>

        <View style={styles.btnRow}>
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.btn}
            onPress={onClose}>
            <SociollaText
              type="bold"
              style={[styles.btnText, { color: Colors.blue1 }]}>
              CANCEL
            </SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.btn}
            onPress={() => onChange(selected)}
            disabled={!selected.length}>
            <SociollaText
              type="bold"
              style={[
                styles.btnText,
                { color: selected.length ? Colors.blue0 : Colors.grey1 },
              ]}>
              SELECT
            </SociollaText>
          </TouchableOpacity>
        </View>
        <PortalHost name="portal_year_multi" />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  datepicker: {
    width: isNative ? dimension.width - 70 : '100%',
    borderRadius: 5,
    overflow: 'hidden',
    backgroundColor: Colors.white,
    marginLeft: isNative ? 35 : 'auto',
    marginTop: isNative ? '20%' : 0,
  },
  datepickerTop: {
    width: '100%',
    alignItems: 'flex-start',
    backgroundColor: Colors.black,
    paddingHorizontal: 15,
    paddingTop: 20,
    paddingBottom: 15,
  },
  yearContainer: {
    marginBottom: 5,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 2,
    backgroundColor: Colors.black6,
  },
  yearText: { fontSize: 14, color: Colors.grey0, lineHeight: 20 },
  headerText: { fontSize: 15, color: Colors.white },
  yearList: {
    position: 'absolute',
    maxHeight: 150,
    left: 15,
    top: 25,
    backgroundColor: Colors.white,
    zIndex: 9,
    elevation: 99,
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 4,
  },
  yearItem: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  monthRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: isNative ? 25 : 15,
    paddingTop: 15,
    paddingBottom: 10,
  },
  arrowBtn: {
    width: 20,
    minWidth: 20,
    height: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  daysRow: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  datesGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  dateCell: {
    width: isNative ? (dimension.width - 105) / 7 : '14.28%',
    minWidth: isNative ? (dimension.width - 105) / 7 : '14.28%',
    height: isNative ? (dimension.width - 105) / 7 : 30,
  },
  rowCenter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingVertical: 10,
  },
  btn: { paddingHorizontal: 15, paddingVertical: 15 },
  btnText: { fontSize: 13, letterSpacing: 0.5 },
});

export default React.memo(
  SociollaMultiDatePicker,
  (prev, next) => prev === next,
);
